import React, { useState, useEffect } from 'react';
import { FaGraduationCap, FaRing, FaUmbrellaBeach, FaPiggyBank, FaPlus, FaSpinner, FaTrash, FaShieldAlt, FaWallet, FaChartLine, FaClock } from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';
import CostOfDelayCalculator from './CostOfDelayCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper component to display mapped investments for a goal
const MappedInvestments = ({ goalId, goalName }) => {
  const [data, setData] = useState({ investments: [], total_invested: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (goalId) {
      fetchInvestments();
    } else {
      setLoading(false);
    }
  }, [goalId]);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/investments/by-goal/${goalId}`);
      setData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0A1929] rounded-lg p-3 animate-pulse">
        <div className="h-4 bg-gray-700 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className="bg-[#0A1929] rounded-lg p-4 border border-[#2D5A8F]">
      <div className="flex items-center space-x-2 mb-2">
        <FaWallet className="text-[#FFD700]" />
        <p className="text-gray-400 text-sm font-medium">Current Investments Mapped</p>
      </div>
      <p className="text-2xl font-bold text-[#34D399]">
        ₹{data.total_invested.toLocaleString('en-IN')}
      </p>
      {data.count > 0 ? (
        <p className="text-gray-500 text-xs mt-1">
          {data.count} investment{data.count > 1 ? 's' : ''} linked to this goal
        </p>
      ) : (
        <p className="text-gray-500 text-xs mt-1">No investments mapped yet</p>
      )}
      {data.investments.length > 0 && (
        <div className="mt-2 space-y-1">
          {data.investments.map((inv, idx) => (
            <div key={idx} className="flex justify-between text-xs text-gray-400">
              <span>{inv.investment_name || inv.investment_type}</span>
              <span>₹{(inv.current_value || 0).toLocaleString('en-IN')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GoalPlanning = ({ clientId, client }) => {
  const [activeGoalType, setActiveGoalType] = useState('education');
  const [loading, setLoading] = useState(false);
  
  const [educationGoals, setEducationGoals] = useState([]);
  const [marriageGoals, setMarriageGoals] = useState([]);
  const [retirementGoal, setRetirementGoal] = useState(null);
  const [customGoals, setCustomGoals] = useState([]);

  useEffect(() => {
    fetchAllGoals();
  }, [clientId]);

  const fetchAllGoals = async () => {
    setLoading(true);
    try {
      const [edu, mar, ret, cust] = await Promise.all([
        axios.get(`${API}/education-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/marriage-goals/${clientId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/retirement-goals/${clientId}`).catch(() => ({ data: null })),
        axios.get(`${API}/custom-goals/${clientId}`).catch(() => ({ data: [] }))
      ]);

      setEducationGoals(edu.data);
      setMarriageGoals(mar.data);
      setRetirementGoal(ret.data);
      setCustomGoals(cust.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'education', label: 'Education', icon: FaGraduationCap },
    { id: 'marriage', label: 'Marriage', icon: FaRing },
    { id: 'retirement', label: 'Retirement', icon: FaPiggyBank },
    { id: 'custom', label: 'Custom Goals', icon: FaUmbrellaBeach }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h2 className="text-2xl font-bold text-[#FFD700] mb-2">🎯 Goal-Based Financial Planning</h2>
        <p className="text-gray-300">Plan for life's major milestones</p>
      </div>

      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl border border-[#2D5A8F] overflow-hidden">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveGoalType(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 font-medium transition-all border-b-2 ${
                  activeGoalType === tab.id
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] border-[#FFD700]'
                    : 'text-gray-400 hover:text-white hover:bg-[#1E3A5F] border-transparent'
                }`}
              >
                <Icon />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeGoalType === 'education' && (
          <EducationGoals clientId={clientId} children={client?.children || []} goals={educationGoals} refresh={fetchAllGoals} />
        )}
        {activeGoalType === 'marriage' && (
          <MarriageGoals clientId={clientId} children={client?.children || []} goals={marriageGoals} refresh={fetchAllGoals} />
        )}
        {activeGoalType === 'retirement' && (
          <RetirementGoal clientId={clientId} client={client} goal={retirementGoal} refresh={fetchAllGoals} />
        )}
        {activeGoalType === 'custom' && (
          <CustomGoals clientId={clientId} goals={customGoals} refresh={fetchAllGoals} />
        )}
      </div>
    </div>
  );
};

// Education Goals Component (keeping existing implementation)
const EducationGoals = ({ clientId, children, goals, refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    child_id: '', child_name: '', current_age: 0, goal_age: 18,
    current_cost: 1500000, inflation_percent: 8, expected_return_percent: 12, existing_corpus: 0, step_up_percent: 10
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/education-goals`, { ...formData, client_id: clientId });
      await refresh();
      setShowForm(false);
      setFormData({ child_id: '', child_name: '', current_age: 0, goal_age: 18, current_cost: 1500000, inflation_percent: 8, expected_return_percent: 12, existing_corpus: 0, step_up_percent: 10 });
    } catch (error) {
      alert('Failed to save education goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this education goal?')) {
      try {
        await axios.delete(`${API}/education-goals/${goalId}`);
        refresh();
      } catch (error) {
        alert('Failed to delete goal');
      }
    }
  };

  const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

  if (children.length === 0) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-12 border border-[#2D5A8F] text-center">
        <FaGraduationCap className="text-[#FFD700] mx-auto mb-4" size={48} />
        <p className="text-gray-400">Add children in client profile first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!showForm && (
        <button onClick={() => setShowForm(true)} className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2">
          <FaPlus /><span>Add Education Goal</span>
        </button>
      )}

      {showForm && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Add Education Goal</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">Select Child</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {children.map((child) => (
                  <button key={child.id} type="button" onClick={() => setFormData({ ...formData, child_id: child.id, child_name: child.name, current_age: child.age })}
                    className={`p-3 rounded-lg text-left transition-all ${formData.child_id === child.id ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold' : 'bg-[#0A1929] text-white hover:bg-[#1E3A5F]'}`}>
                    {child.name} ({child.age} years)
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-300 text-sm mb-2">Goal Age</label>
                <input type="number" value={formData.goal_age} onChange={(e) => setFormData({ ...formData, goal_age: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Current Cost (₹)</label>
                <input type="number" value={formData.current_cost} onChange={(e) => setFormData({ ...formData, current_cost: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Inflation (%)</label>
                <input type="number" step="0.1" value={formData.inflation_percent} onChange={(e) => setFormData({ ...formData, inflation_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Expected Return (%)</label>
                <input type="number" step="0.1" value={formData.expected_return_percent} onChange={(e) => setFormData({ ...formData, expected_return_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div className="col-span-2">
                <label className="block text-gray-300 text-sm mb-2">Annual SIP Step-Up (%)</label>
                <input type="number" step="1" value={formData.step_up_percent} onChange={(e) => setFormData({ ...formData, step_up_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" />
                <p className="text-gray-400 text-xs mt-1">Increase SIP by this % every year (e.g., 10% step-up means SIP increases annually)</p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F]">Cancel</button>
              <button type="submit" disabled={saving || !formData.child_id} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg disabled:opacity-50">
                {saving ? 'Saving...' : 'Calculate & Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-12 border border-[#2D5A8F] text-center">
          <FaGraduationCap className="text-[#FFD700] mx-auto mb-4" size={48} />
          <p className="text-gray-400">No education goals added yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {goals.map((goal) => (
            <div key={goal.id} className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white">{goal.child_name}'s Education</h4>
                  <p className="text-gray-400 text-sm">Goal Age: {goal.goal_age} • Time: {goal.time_horizon_years} years</p>
                </div>
                <button onClick={() => handleDelete(goal.id)} className="text-red-400 hover:text-red-300 p-2">
                  <FaTrash />
                </button>
              </div>
              
              {/* Main Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Future Value</p>
                  <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(goal.future_goal_value)}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Monthly SIP</p>
                  <p className="text-2xl font-bold text-white">₹{Math.round(goal.required_monthly_sip).toLocaleString()}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Return</p>
                  <p className="text-2xl font-bold text-white">{goal.expected_return_percent}%</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-gray-400 text-sm mb-1">Life Insurance Needed</p>
                  <p className="text-2xl font-bold text-blue-400">{formatCurrency(goal.life_insurance_required || goal.future_goal_value)}</p>
                </div>
                <MappedInvestments goalId={goal.id} goalName={`${goal.child_name} Education`} />
              </div>

              {/* Step-Up SIP Section */}
              {goal.step_up_percent > 0 && goal.step_up_starting_sip > 0 && (
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-6">
                  <p className="text-green-400 font-semibold mb-2">Step-Up SIP ({goal.step_up_percent}% annual increase)</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Start with</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Final year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_final_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Start {Math.round(((goal.required_monthly_sip - goal.step_up_starting_sip) / goal.required_monthly_sip) * 100)}% lower</p>
                      <p className="text-green-400 font-bold">Save ₹{Math.round(goal.required_monthly_sip - goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost of Delay Section */}
              {goal.delay_1_year_sip > 0 && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                  <p className="text-red-400 font-semibold mb-3">Cost of Delay - Don't Wait!</p>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 1 Year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_1_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_1_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 2 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_2_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_2_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 3 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_3_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_3_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 5 Years</p>
                      <p className="text-white font-bold">{goal.delay_5_year_sip === Infinity ? 'N/A' : `₹${Math.round(goal.delay_5_year_sip).toLocaleString()}/m`}</p>
                      <p className="text-red-400 text-xs">{goal.delay_5_year_sip !== Infinity ? `+₹${Math.round(goal.delay_5_year_sip - goal.required_monthly_sip).toLocaleString()}` : 'Too late!'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* LIVE Cost of Delay Calculator for Education */}
              <div className="mb-6">
                <CostOfDelayCalculator
                  futureValue={goal.future_goal_value || goal.future_cost}
                  years={goal.time_horizon_years || (goal.goal_age - goal.current_age)}
                  expectedReturn={goal.expected_return_percent}
                  existingCorpus={goal.existing_corpus}
                  originalSip={goal.required_monthly_sip}
                  goalName={`${goal.child_name}'s Education`}
                />
              </div>

              {/* Chart Section */}
              {goal.sip_projection && goal.sip_projection.length > 0 && (
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-[#FFD700] font-semibold mb-4">SIP Growth Projection</p>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={goal.sip_projection}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
                      <XAxis dataKey="year" stroke="#9CA3AF" tick={{fontSize: 12}} label={{ value: 'Year', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }} />
                      <YAxis stroke="#9CA3AF" tick={{fontSize: 12}} tickFormatter={(val) => `₹${(val/100000).toFixed(0)}L`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
                        labelStyle={{ color: '#FFD700' }}
                        formatter={(value) => [`₹${Math.round(value).toLocaleString()}`, '']}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="invested" stroke="#60A5FA" strokeWidth={2} name="Total Invested" dot={false} />
                      <Line type="monotone" dataKey="corpus" stroke="#34D399" strokeWidth={2} name="Expected Corpus" dot={false} />
                      <Line type="monotone" dataKey="target" stroke="#FFD700" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Marriage Goals Component (similar structure)
const MarriageGoals = ({ clientId, children, goals, refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    child_id: '', child_name: '', current_age: 0, marriage_age: 25,
    current_cost: 2000000, inflation_percent: 7, expected_return_percent: 12, existing_corpus: 0, step_up_percent: 10
  });

  const handleDelete = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this marriage goal?')) {
      try {
        await axios.delete(`${API}/marriage-goals/${goalId}`);
        refresh();
      } catch (error) {
        alert('Failed to delete goal');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/marriage-goals`, { ...formData, client_id: clientId });
      await refresh();
      setShowForm(false);
    } catch (error) {
      alert('Failed to save marriage goal');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

  if (children.length === 0) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-12 border border-[#2D5A8F] text-center">
        <FaRing className="text-[#FFD700] mx-auto mb-4" size={48} />
        <p className="text-gray-400">Add children in client profile first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!showForm && (
        <button onClick={() => setShowForm(true)} className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg flex items-center justify-center space-x-2">
          <FaPlus /><span>Add Marriage Goal</span>
        </button>
      )}

      {showForm && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Add Marriage Goal</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {children.map((child) => (
                <button key={child.id} type="button" onClick={() => setFormData({ ...formData, child_id: child.id, child_name: child.name, current_age: child.age })}
                  className={`p-3 rounded-lg ${formData.child_id === child.id ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold' : 'bg-[#0A1929] text-white'}`}>
                  {child.name} ({child.age} years)
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-300 text-sm mb-2">Marriage Age</label>
                <input type="number" value={formData.marriage_age} onChange={(e) => setFormData({ ...formData, marriage_age: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Current Cost (₹)</label>
                <input type="number" value={formData.current_cost} onChange={(e) => setFormData({ ...formData, current_cost: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" /></div>
              <div className="col-span-2">
                <label className="block text-gray-300 text-sm mb-2">Annual SIP Step-Up (%)</label>
                <input type="number" step="1" value={formData.step_up_percent} onChange={(e) => setFormData({ ...formData, step_up_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700]" />
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 bg-[#1E3A5F] text-white rounded-lg">Cancel</button>
              <button type="submit" disabled={saving || !formData.child_id} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {goals.length > 0 && (
        <div className="space-y-6">
          {goals.map((goal) => (
            <div key={goal.id} className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white">{goal.child_name}'s Marriage</h4>
                  <p className="text-gray-400 text-sm">Marriage Age: {goal.marriage_age} • Time: {goal.time_horizon_years} years</p>
                </div>
                <button onClick={() => handleDelete(goal.id)} className="text-red-400 hover:text-red-300 p-2">
                  <FaTrash />
                </button>
              </div>
              
              {/* Main Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Future Cost</p>
                  <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(goal.future_cost)}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Monthly SIP</p>
                  <p className="text-2xl font-bold text-white">₹{Math.round(goal.required_monthly_sip).toLocaleString()}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Time Horizon</p>
                  <p className="text-2xl font-bold text-white">{goal.time_horizon_years}y</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4 border-l-4 border-blue-500">
                  <p className="text-gray-400 text-sm">Life Insurance Needed</p>
                  <p className="text-2xl font-bold text-blue-400">{formatCurrency(goal.life_insurance_required || goal.future_cost)}</p>
                </div>
                <MappedInvestments goalId={goal.id} goalName={`${goal.child_name} Marriage`} />
              </div>

              {/* Step-Up SIP Section */}
              {goal.step_up_percent > 0 && goal.step_up_starting_sip > 0 && (
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-6">
                  <p className="text-green-400 font-semibold mb-2">Step-Up SIP ({goal.step_up_percent}% annual increase)</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Start with</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Final year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_final_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Start {Math.round(((goal.required_monthly_sip - goal.step_up_starting_sip) / goal.required_monthly_sip) * 100)}% lower</p>
                      <p className="text-green-400 font-bold">Save ₹{Math.round(goal.required_monthly_sip - goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost of Delay Section */}
              {goal.delay_1_year_sip > 0 && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                  <p className="text-red-400 font-semibold mb-3">Cost of Delay - Don't Wait!</p>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 1 Year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_1_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_1_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 2 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_2_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_2_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 3 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_3_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_3_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 5 Years</p>
                      <p className="text-white font-bold">{goal.delay_5_year_sip === Infinity ? 'N/A' : `₹${Math.round(goal.delay_5_year_sip).toLocaleString()}/m`}</p>
                      <p className="text-red-400 text-xs">{goal.delay_5_year_sip !== Infinity ? `+₹${Math.round(goal.delay_5_year_sip - goal.required_monthly_sip).toLocaleString()}` : 'Too late!'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* LIVE Cost of Delay Calculator for Marriage */}
              <div className="mb-6">
                <CostOfDelayCalculator
                  futureValue={goal.future_cost}
                  years={goal.marriage_age - goal.current_age}
                  expectedReturn={goal.expected_return_percent}
                  existingCorpus={goal.existing_corpus}
                  originalSip={goal.required_monthly_sip}
                  goalName={`${goal.child_name}'s Marriage`}
                />
              </div>

              {/* Chart Section */}
              {goal.sip_projection && goal.sip_projection.length > 0 && (
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-[#FFD700] font-semibold mb-4">SIP Growth Projection</p>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={goal.sip_projection}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
                      <XAxis dataKey="year" stroke="#9CA3AF" tick={{fontSize: 12}} label={{ value: 'Year', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }} />
                      <YAxis stroke="#9CA3AF" tick={{fontSize: 12}} tickFormatter={(val) => `₹${(val/100000).toFixed(0)}L`} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
                        labelStyle={{ color: '#FFD700' }}
                        formatter={(value) => [`₹${Math.round(value).toLocaleString()}`, '']}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="invested" stroke="#60A5FA" strokeWidth={2} name="Total Invested" dot={false} />
                      <Line type="monotone" dataKey="corpus" stroke="#34D399" strokeWidth={2} name="Expected Corpus" dot={false} />
                      <Line type="monotone" dataKey="target" stroke="#FFD700" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Mapped Investments */}
              <MappedInvestments goalId={goal.id} goalName={`${goal.child_name}'s Marriage`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Retirement Goal Component
const RetirementGoal = ({ clientId, client, goal, refresh }) => {
  const [showForm, setShowForm] = useState(!goal);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    current_age: client?.age || 35, retirement_age: 60, monthly_expense: 50000,
    life_expectancy: 85, inflation_percent: 6, pre_retirement_return: 12,
    post_retirement_return: 8, existing_corpus: 0, current_sip: 0, step_up_percent: 10
  });

  useEffect(() => {
    if (goal) {
      setFormData(goal);
      setShowForm(false);
    }
  }, [goal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/retirement-goals`, { ...formData, client_id: clientId });
      await refresh();
      setShowForm(false);
    } catch (error) {
      alert('Failed to save retirement goal');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `₹${(value / 10000000).toFixed(2)}Cr`;

  return (
    <div className="space-y-6">
      {!goal && !showForm && (
        <button onClick={() => setShowForm(true)} className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg flex items-center justify-center space-x-2">
          <FaPlus /><span>Plan Retirement</span>
        </button>
      )}

      {showForm && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Retirement Planning</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-300 text-sm mb-2">Current Age</label>
                <input type="number" value={formData.current_age} onChange={(e) => setFormData({ ...formData, current_age: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Retirement Age</label>
                <input type="number" value={formData.retirement_age} onChange={(e) => setFormData({ ...formData, retirement_age: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Monthly Expense (₹)</label>
                <input type="number" value={formData.monthly_expense} onChange={(e) => setFormData({ ...formData, monthly_expense: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Life Expectancy</label>
                <input type="number" value={formData.life_expectancy} onChange={(e) => setFormData({ ...formData, life_expectancy: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Existing Corpus (₹)</label>
                <input type="number" value={formData.existing_corpus} onChange={(e) => setFormData({ ...formData, existing_corpus: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Current SIP (₹/month)</label>
                <input type="number" value={formData.current_sip} onChange={(e) => setFormData({ ...formData, current_sip: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" /></div>
              <div className="col-span-2">
                <label className="block text-gray-300 text-sm mb-2">Annual SIP Step-Up (%)</label>
                <input type="number" step="1" value={formData.step_up_percent} onChange={(e) => setFormData({ ...formData, step_up_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" />
                <p className="text-gray-400 text-xs mt-1">Increase SIP by this % every year to reduce initial investment requirement</p>
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 bg-[#1E3A5F] text-white rounded-lg">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg disabled:opacity-50">
                {saving ? 'Calculating...' : 'Calculate Retirement'}
              </button>
            </div>
          </form>
        </div>
      )}

      {goal && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
            <h4 className="text-xl font-bold text-white mb-4">Retirement Corpus Required</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-[#0A1929] rounded-lg p-4">
                <p className="text-gray-400 text-sm">Target Corpus</p>
                <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(goal.target_corpus)}</p>
              </div>
              <div className="bg-[#0A1929] rounded-lg p-4">
                <p className="text-gray-400 text-sm">Projected Corpus</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(goal.projected_corpus)}</p>
              </div>
              <div className="bg-[#0A1929] rounded-lg p-4">
                <p className="text-gray-400 text-sm">Additional SIP Needed</p>
                <p className="text-2xl font-bold text-[#FFD700]">₹{Math.round(goal.sip_needed).toLocaleString()}/m</p>
              </div>
              <MappedInvestments goalId={goal.id} goalName="Retirement" />
            </div>
            {goal.step_up_percent > 0 && goal.step_up_starting_sip > 0 && (
              <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
                <p className="text-green-400 font-semibold mb-2">Step-Up SIP ({goal.step_up_percent}% annual increase)</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Start with additional</p>
                    <p className="text-white font-bold">₹{Math.round(goal.step_up_starting_sip).toLocaleString()}/m</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Final year SIP</p>
                    <p className="text-white font-bold">₹{Math.round(goal.step_up_final_sip).toLocaleString()}/m</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Start {Math.round(((goal.sip_needed - goal.step_up_starting_sip) / goal.sip_needed) * 100)}% lower</p>
                    <p className="text-green-400 font-bold">Save ₹{Math.round(goal.sip_needed - goal.step_up_starting_sip).toLocaleString()}/m</p>
                  </div>
                </div>
              </div>
            )}

            {/* Cost of Delay Section for Retirement */}
            {goal.delay_1_year_sip > 0 && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
                <p className="text-red-400 font-semibold mb-3">Cost of Delay - Don't Wait!</p>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="bg-[#0A1929] rounded p-3 text-center">
                    <p className="text-gray-400">Delay 1 Year</p>
                    <p className="text-white font-bold">₹{Math.round(goal.delay_1_year_sip).toLocaleString()}/m</p>
                    <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_1_year_sip - goal.sip_needed).toLocaleString()}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded p-3 text-center">
                    <p className="text-gray-400">Delay 2 Years</p>
                    <p className="text-white font-bold">₹{Math.round(goal.delay_2_year_sip).toLocaleString()}/m</p>
                    <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_2_year_sip - goal.sip_needed).toLocaleString()}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded p-3 text-center">
                    <p className="text-gray-400">Delay 3 Years</p>
                    <p className="text-white font-bold">₹{Math.round(goal.delay_3_year_sip).toLocaleString()}/m</p>
                    <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_3_year_sip - goal.sip_needed).toLocaleString()}</p>
                  </div>
                  <div className="bg-[#0A1929] rounded p-3 text-center">
                    <p className="text-gray-400">Delay 5 Years</p>
                    <p className="text-white font-bold">{goal.delay_5_year_sip === Infinity || !isFinite(goal.delay_5_year_sip) ? 'N/A' : `₹${Math.round(goal.delay_5_year_sip).toLocaleString()}/m`}</p>
                    <p className="text-red-400 text-xs">{goal.delay_5_year_sip !== Infinity && isFinite(goal.delay_5_year_sip) ? `+₹${Math.round(goal.delay_5_year_sip - goal.sip_needed).toLocaleString()}` : 'Too late!'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* LIVE Cost of Delay Calculator for Retirement */}
            <CostOfDelayCalculator
              futureValue={goal.target_corpus}
              years={goal.retirement_age - goal.current_age}
              expectedReturn={goal.pre_retirement_return}
              existingCorpus={goal.existing_corpus}
              originalSip={goal.sip_needed + goal.current_sip}
              goalName="Retirement"
            />
          </div>
          <button onClick={() => setShowForm(true)} className="w-full py-3 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2D5A8F]">
            Edit Retirement Plan
          </button>
        </div>
      )}
    </div>
  );
};

// Custom Goals Component
const CustomGoals = ({ clientId, goals, refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    goal_name: '', current_cost: 0, years_to_goal: 5,
    inflation_percent: 6, expected_return_percent: 12, existing_corpus: 0, step_up_percent: 10
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/custom-goals`, { ...formData, client_id: clientId });
      await refresh();
      setShowForm(false);
      setFormData({ goal_name: '', current_cost: 0, years_to_goal: 5, inflation_percent: 6, expected_return_percent: 12, existing_corpus: 0, step_up_percent: 10 });
    } catch (error) {
      alert('Failed to save custom goal');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

  return (
    <div className="space-y-6">
      {!showForm && (
        <button onClick={() => setShowForm(true)} className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg flex items-center justify-center space-x-2">
          <FaPlus /><span>Add Custom Goal</span>
        </button>
      )}

      {showForm && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-xl font-bold text-[#FFD700] mb-4">Add Custom Goal</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-gray-300 text-sm mb-2">Goal Name</label>
              <input type="text" value={formData.goal_name} onChange={(e) => setFormData({ ...formData, goal_name: e.target.value })} placeholder="Car, Vacation, House Down Payment" className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-gray-300 text-sm mb-2">Current Cost (₹)</label>
                <input type="number" value={formData.current_cost} onChange={(e) => setFormData({ ...formData, current_cost: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" required /></div>
              <div><label className="block text-gray-300 text-sm mb-2">Years to Goal</label>
                <input type="number" value={formData.years_to_goal} onChange={(e) => setFormData({ ...formData, years_to_goal: parseInt(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" required /></div>
              <div className="col-span-2">
                <label className="block text-gray-300 text-sm mb-2">Annual SIP Step-Up (%)</label>
                <input type="number" step="1" value={formData.step_up_percent} onChange={(e) => setFormData({ ...formData, step_up_percent: parseFloat(e.target.value) })} className="w-full px-4 py-3 bg-[#0A1929] border border-[#2D5A8F] rounded-lg text-white" />
              </div>
            </div>
            <div className="flex space-x-4">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-3 bg-[#1E3A5F] text-white rounded-lg">Cancel</button>
              <button type="submit" disabled={saving} className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Goal'}
              </button>
            </div>
          </form>
        </div>
      )}

      {goals.length > 0 && (
        <div className="space-y-4">
          {goals.map((goal) => (
            <div key={goal.id} className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
              <h4 className="text-xl font-bold text-white mb-2">{goal.goal_name}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Future Cost</p>
                  <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(goal.future_cost)}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Regular SIP</p>
                  <p className="text-2xl font-bold text-white">₹{Math.round(goal.required_monthly_sip).toLocaleString()}</p>
                </div>
                <div className="bg-[#0A1929] rounded-lg p-4">
                  <p className="text-gray-400 text-sm">Time Horizon</p>
                  <p className="text-2xl font-bold text-white">{goal.years_to_goal}y</p>
                </div>
                <MappedInvestments goalId={goal.id} goalName={goal.goal_name} />
              </div>
              {goal.step_up_percent > 0 && goal.step_up_starting_sip > 0 && (
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-4">
                  <p className="text-green-400 font-semibold mb-2">Step-Up SIP ({goal.step_up_percent}% annual increase)</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Start with</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Final year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.step_up_final_sip).toLocaleString()}/m</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Start {Math.round(((goal.required_monthly_sip - goal.step_up_starting_sip) / goal.required_monthly_sip) * 100)}% lower</p>
                      <p className="text-green-400 font-bold">Save ₹{Math.round(goal.required_monthly_sip - goal.step_up_starting_sip).toLocaleString()}/m</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost of Delay Section for Custom Goals */}
              {goal.delay_1_year_sip > 0 && (
                <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-4">
                  <p className="text-red-400 font-semibold mb-3">Cost of Delay - Don't Wait!</p>
                  <div className="grid grid-cols-4 gap-3 text-sm">
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 1 Year</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_1_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_1_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 2 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_2_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_2_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 3 Years</p>
                      <p className="text-white font-bold">₹{Math.round(goal.delay_3_year_sip).toLocaleString()}/m</p>
                      <p className="text-red-400 text-xs">+₹{Math.round(goal.delay_3_year_sip - goal.required_monthly_sip).toLocaleString()}</p>
                    </div>
                    <div className="bg-[#0A1929] rounded p-3 text-center">
                      <p className="text-gray-400">Delay 5 Years</p>
                      <p className="text-white font-bold">{goal.delay_5_year_sip === Infinity || !isFinite(goal.delay_5_year_sip) ? 'N/A' : `₹${Math.round(goal.delay_5_year_sip).toLocaleString()}/m`}</p>
                      <p className="text-red-400 text-xs">{goal.delay_5_year_sip !== Infinity && isFinite(goal.delay_5_year_sip) ? `+₹${Math.round(goal.delay_5_year_sip - goal.required_monthly_sip).toLocaleString()}` : 'Too late!'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* LIVE Cost of Delay Calculator for Custom Goals */}
              <CostOfDelayCalculator
                futureValue={goal.future_cost}
                years={goal.years_to_goal}
                expectedReturn={goal.expected_return_percent}
                existingCorpus={goal.existing_corpus}
                originalSip={goal.required_monthly_sip}
                goalName={goal.goal_name}
              />

              {/* Life Insurance Required */}
              {goal.life_insurance_required > 0 && (
                <div className="mt-4 bg-blue-500/10 border border-blue-500 rounded-lg p-4 flex items-center space-x-3">
                  <FaShieldAlt className="text-blue-400" size={24} />
                  <div>
                    <p className="text-blue-400 font-semibold">Life Insurance to Protect This Goal</p>
                    <p className="text-white font-bold text-xl">₹{Math.round(goal.life_insurance_required).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GoalPlanning;
